import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { useSession } from '../auth/session';
import { Alert } from '../ui';

interface PendingAddress { id: string; retailerId: string; retailer: string; kind: 'BILLING' | 'SHIPPING' | null; requestedAt: string }
interface PendingOrder { id: string; orderId: string; reference: string; retailer: string; requestedAt: string }

/**
 * Staff are told, on every Admin page, when a retailer is waiting on them: an address change or a
 * change to an order's quantity or size. Each line links to where it is decided. A request that
 * nobody has to look for is one that gets answered.
 *
 * Polled once a minute like the notification bell, and only for what the signed-in person may
 * decide: the server refuses the rest, and a refusal here must never break the page, so errors are
 * swallowed (the banner is chrome).
 */
export function PendingApprovals() {
  const { me, portal, can } = useSession();
  const scope = ['portal', portal?.code ?? null, 'pending-approvals'];
  const addresses = useQuery({
    queryKey: [...scope, 'address'], retry: false, refetchInterval: 60_000,
    enabled: me?.kind === 'internal' && can('retailer.view'),
    queryFn: () => api.get<PendingAddress[]>('/retailers/address-requests/pending'),
  });
  const orders = useQuery({
    queryKey: [...scope, 'orders'], retry: false, refetchInterval: 60_000,
    enabled: me?.kind === 'internal' && can('order.view'),
    queryFn: () => api.get<PendingOrder[]>('/orders/change-requests/pending'),
  });

  const a = addresses.data ?? [];
  const o = orders.data ?? [];
  if (a.length + o.length === 0) return null;

  return (
    <Alert tone="us">
      <b>Waiting for your approval ({a.length + o.length})</b>
      <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
        {a.map((r) => (
          <li key={r.id}>
            <Link to={`/retailers/${r.retailerId}`}>
              {r.retailer}: {r.kind === 'BILLING' ? 'billing' : r.kind === 'SHIPPING' ? 'delivery' : ''} address change
            </Link>
          </li>
        ))}
        {o.map((r) => (
          <li key={r.id}>
            <Link to={`/orders/${r.orderId}`}>{r.reference} ({r.retailer}): quantity or size change</Link>
          </li>
        ))}
      </ul>
    </Alert>
  );
}
